import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import EmailIcon from "@mui/icons-material/Email";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import PersonIcon from "@mui/icons-material/Person";
import FileDownloadDoneIcon from "@mui/icons-material/FileDownloadDone";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import GitHubIcon from "@mui/icons-material/GitHub";
import InstagramIcon from "@mui/icons-material/Instagram";
import emailjs from "@emailjs/browser";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function Contact() {
  const [mailSent, setMailSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleSubmit = (e) => {
    setLoading(true);
    e.preventDefault();
    const { from_name, reply_to, message } = e.target.elements;

    const REACT_APP_SERVICEID = process.env.REACT_APP_SERVICEID;
    const REACT_APP_TEMPLATE = process.env.REACT_APP_TEMPLATE;
    const REACT_APP_PUBLICKEY = process.env.REACT_APP_PUBLICKEY;

    if (!REACT_APP_SERVICEID || !REACT_APP_TEMPLATE || !REACT_APP_PUBLICKEY) {
      setLoading(false);
      console.error("EmailJS config is missing", {
        hasServiceId: Boolean(REACT_APP_SERVICEID),
        hasTemplateId: Boolean(REACT_APP_TEMPLATE),
        hasPublicKey: Boolean(REACT_APP_PUBLICKEY),
      });
      toast.error("Email service is not configured correctly.", {
        position: "bottom-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }

    const templateParams = {
      to_name: "Khashayar",
      from_name: from_name.value,
      reply_to: reply_to.value,
      message: message.value,
    };

    emailjs
      .send(
        REACT_APP_SERVICEID,
        REACT_APP_TEMPLATE,
        templateParams,
        REACT_APP_PUBLICKEY
      )
      .then(
        (response) => {
          setMailSent(true);
          setLoading(false);
          toast.success("Message Sent Successfully!", {
            position: "bottom-center",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          });
        },
        (err) => {
          setLoading(false);
          console.error("EmailJS send failed", err);
          toast.error(
            `Email failed${
              err?.status || err?.text
                ? `: ${err?.status ?? ""} ${err?.text ?? ""}`.trim()
                : "!"
            }`,
            {
            position: "bottom-center",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
            }
          );
        }
      );
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.6, -0.05, 0.01, 0.99],
      },
    },
  };

  const socialLinks = [
    {
      icon: GitHubIcon,
      url: "https://github.com/khmorad",
      label: "GitHub",
      color: "hover:text-gray-400",
    },
    {
      icon: LinkedInIcon,
      url: "https://www.linkedin.com/in/kmoradpour/",
      label: "LinkedIn",
      color: "hover:text-blue-400",
    },
    {
      icon: InstagramIcon,
      url: "",
      label: "Instagram",
      color: "hover:text-pink-400",
    },
  ];

  return (
    <div
      id="contact"
      ref={ref}
      className="min-h-screen py-20 px-4 relative overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        className="relative z-10 max-w-7xl mx-auto"
      >
        <motion.div variants={itemVariants} className="text-center mb-16">
          <h2 className="head text-5xl md:text-6xl lg:text-7xl font-bold mb-4 gradient-text">
            Get In Touch
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full mb-6"></div>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
            Ready to collaborate on your next project? Let's discuss how we can
            bring your ideas to life.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Contact Form */}
          <motion.div variants={itemVariants}>
            <div className="glass p-8 rounded-3xl">
              <form onSubmit={handleSubmit} className="space-y-6">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <label className="block text-xl font-semibold mb-3 text-white">
                    Send me a message
                  </label>
                  <div className="w-16 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mb-6"></div>
                </motion.div>

                <motion.div
                  className="relative"
                  whileFocus={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="flex items-center glass rounded-2xl overflow-hidden border border-white/10 focus-within:border-blue-500/50 transition-colors">
                    <span className="px-4 py-3 text-gray-400">
                      <PersonIcon />
                    </span>
                    <input
                      disabled={mailSent}
                      readOnly={mailSent}
                      id="from_name"
                      name="from_name"
                      required
                      type="text"
                      className="flex-1 bg-transparent py-3 pr-4 text-white placeholder-gray-400 focus:outline-none"
                      placeholder="Your Name"
                    />
                  </div>
                </motion.div>

                <motion.div
                  className="relative"
                  whileFocus={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="flex items-center glass rounded-2xl overflow-hidden border border-white/10 focus-within:border-blue-500/50 transition-colors">
                    <span className="px-4 py-3 text-gray-400">
                      <AlternateEmailIcon />
                    </span>
                    <input
                      disabled={mailSent}
                      readOnly={mailSent}
                      id="reply_to"
                      name="reply_to"
                      required
                      minLength={5}
                      type="email"
                      className="flex-1 bg-transparent py-3 pr-4 text-white placeholder-gray-400 focus:outline-none"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </motion.div>

                <motion.div
                  whileFocus={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <textarea
                    id="message"
                    name="message"
                    disabled={mailSent}
                    readOnly={mailSent}
                    rows="6"
                    required
                    minLength={10}
                    placeholder="Tell me about your project or just say hello..."
                    className="w-full glass rounded-2xl p-4 text-white placeholder-gray-400 border border-white/10 focus:border-blue-500/50 focus:outline-none resize-none transition-colors"
                  ></textarea>
                </motion.div>

                <motion.div className="pt-4">
                  {!mailSent && !loading && (
                    <motion.button
                      type="submit"
                      className="btn-primary w-full"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <span className="flex items-center justify-center gap-2">
                        <EmailIcon />
                        Send Message
                      </span>
                    </motion.button>
                  )}

                  {loading && (
                    <motion.button
                      disabled
                      type="button"
                      className="w-full py-3 px-6 bg-blue-500/50 text-white rounded-2xl font-semibold flex items-center justify-center gap-3"
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Sending...
                    </motion.button>
                  )}

                  {mailSent && !loading && (
                    <motion.button
                      type="button"
                      disabled
                      className="w-full py-3 px-6 bg-green-500 text-white rounded-2xl font-semibold flex items-center justify-center gap-2"
                      initial={{ scale: 0.8 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <FileDownloadDoneIcon />
                      Message Sent Successfully!
                    </motion.button>
                  )}
                </motion.div>
              </form>
            </div>
          </motion.div>

          {/* Contact Info & Animation */}
          <motion.div variants={itemVariants} className="space-y-8">
            {windowWidth > 980 && (
              <motion.div
                className="text-center"
                animate={{ y: [0, -10, 0] }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <lottie-player
                  autoplay
                  loop
                  mode="bounce"
                  src="https://assets9.lottiefiles.com/packages/lf20_3rqwsqnj.json"
                  style={{ width: "100%", maxWidth: "350px", margin: "0 auto" }}
                />
              </motion.div>
            )}

            <div className="text-center space-y-6">
              <motion.h3
                className="text-3xl font-bold gradient-text"
                whileHover={{ scale: 1.05 }}
              >
                Let's Connect
              </motion.h3>

              <p className="text-gray-400 leading-relaxed max-w-md mx-auto">
                I'm always open to discussing new opportunities, interesting
                projects, or just having a chat about technology and innovation.
              </p>

              <motion.div
                className="flex justify-center gap-6 pt-4"
                variants={containerVariants}
              >
                {socialLinks.map((social, index) => {
                  const IconComponent = social.icon;
                  return (
                    <motion.div
                      key={index}
                      variants={itemVariants}
                      whileHover={{ scale: 1.2, y: -5 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <button
                        onClick={() =>
                          social.url && window.open(social.url, "_blank")
                        }
                        className={`glass w-14 h-14 rounded-2xl flex items-center justify-center text-white ${social.color} transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/20`}
                        aria-label={social.label}
                      >
                        <IconComponent className="text-xl" />
                      </button>
                    </motion.div>
                  );
                })}
              </motion.div>

              <motion.div
                className="glass p-6 rounded-2xl mt-8"
                whileHover={{ scale: 1.02 }}
              >
                <h4 className="text-lg font-semibold text-white mb-2">
                  Quick Response
                </h4>
                <p className="text-gray-400 text-sm">
                  I typically respond to messages within 24 hours. For urgent
                  matters, feel free to reach out on LinkedIn.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      <ToastContainer
        position="bottom-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </div>
  );
}
